import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import usePageTitle from '../hooks/usePageTitle';
import useSocialMeta from '../hooks/useSocialMeta';
import {
  Building2,
  MapPin,
  Calculator,
  Users,
  BookOpen,
  Map,
  ChevronRight,
  Landmark,
  Home as HomeIcon,
  ArrowRight,
  Sparkles,
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import HomeAdCarousel from '../components/HomeAdCarousel';
import HomeHero from '../components/HomeHero';
import HomeQuickFilters from '../components/HomeQuickFilters';
import RecentlyViewedSection from '../components/RecentlyViewedSection';
import HomeAdviceSection from '../components/HomeAdviceSection';
import MobileCarousel from '../components/MobileCarousel';
import PaginatedPropertyGrid from '../components/PaginatedPropertyGrid';
import { regions } from '../data/mockData';
import { propertyAPI } from '../utils/api';

// "+ Voir plus" on phones: once featured are exhausted, continue with other published listings.
const fetchMoreListings = (skip) =>
  propertyAPI.getAll({ skip, limit: 12 }).then((r) => (Array.isArray(r.data) ? r.data : []));

/**
 * Section 1 — Propriétés en vedette
 * 3 cartes par page × 3 pages = 9 biens max, navigation par flèches + dots.
 */
const FeaturedSection = ({ properties, loading }) => (
  <section id="listings-section" className="mb-20" data-testid="home-featured">
    <PaginatedPropertyGrid
      properties={properties}
      loading={loading}
      pageSize={3}
      maxItems={9}
      autoplayMs={15000}
      testIdPrefix="featured"
      mobileLimit={5}
      fetchMore={fetchMoreListings}
      emptyLabel="Aucune propriété en vedette pour le moment."
      renderControls={({ prevButton, nextButton }) => (
        <div className="flex items-end justify-between gap-4 mb-6">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-brand-600 mb-2">
              <Sparkles className="h-3.5 w-3.5" />
              Propriétés en vedette
            </div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-stone-900 leading-tight">
              Découvrez nos biens d&apos;exception
            </h2>
            <p className="text-stone-500 text-sm sm:text-base mt-2">
              Un aperçu privilégié des meilleures opportunités, sélectionnées par nos agents partenaires.
            </p>
          </div>
          <div className="hidden md:flex items-center gap-2 shrink-0">
            {prevButton}
            {nextButton}
          </div>
        </div>
      )}
    />
  </section>
);

/**
 * Section 2 — Recommandations pour vous
 * Même pattern : 3 par page × 3 pages = 9 biens max.
 */
const RecommendationsSection = ({ properties, loading }) => (
  <section className="mb-20" data-testid="home-recommendations">
    <PaginatedPropertyGrid
      properties={properties}
      loading={loading}
      pageSize={3}
      maxItems={9}
      autoplayMs={15000}
      testIdPrefix="recommendations"
      emptyLabel="Aucune recommandation pour le moment."
      renderControls={({ prevButton, nextButton }) => (
        <div className="flex items-end justify-between gap-4 mb-6">
          <div className="max-w-2xl">
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-stone-900 leading-tight">
              Recommandations pour vous
            </h2>
            <p className="text-stone-500 text-sm sm:text-base mt-2">
              Sélectionnées d&apos;après vos centres d&apos;intérêt et les biens les plus consultés.
            </p>
          </div>
          <div className="hidden md:flex items-center gap-3 shrink-0">
            <Link
              to="/properties"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-600 hover:text-brand-700 border border-brand-200 hover:border-brand-300 hover:bg-brand-50 transition-colors rounded-full px-4 py-2"
              data-testid="recommendations-see-more"
            >
              Voir plus de recommandations
              <ArrowRight className="h-4 w-4" />
            </Link>
            <div className="flex items-center gap-2">
              {prevButton}
              {nextButton}
            </div>
          </div>
        </div>
      )}
    />
  </section>
);

/**
/**
 * Section 3 — Liens rapides (Acheter / Crédit / Vendre / Louer)
 * Style inspiré du modèle Redfin (REDESIGN.pdf) :
 * - Carte blanche bord arrondi
 * - Top : ovale blush (rose pâle) contenant l'illustration (icône stylisée)
 * - Titre centré (display, bold)
 * - Description centrée
 * - CTA bouton outlined en bas
 */
const QuickLinks = () => {
  const services = [
    {
      key: 'buy',
      illustration: '/illustrations/buy.png?v=2',
      illustrationAlt: 'Icône Acheter un bien immobilier',
      title: 'Acheter',
      description:
        "Nos agents partenaires figurent parmi les plus expérimentés du Cameroun et vous aident à trouver le bien idéal au meilleur prix.",
      cta: 'Trouver un agent',
      to: '/agents',
    },
    {
      key: 'loan',
      illustration: '/illustrations/loan.png?v=2',
      illustrationAlt: 'Icône Crédit immobilier',
      title: 'Crédit immobilier',
      description:
        "Simulez votre prêt et obtenez une pré-qualification rapide grâce à nos partenaires bancaires aux meilleurs taux du marché.",
      cta: 'Se pré-qualifier',
      to: '/loan-calculator',
    },
    {
      key: 'sell',
      illustration: '/illustrations/sell.png?v=2',
      illustrationAlt: 'Icône Vendre un bien immobilier',
      title: 'Vendre',
      description:
        "Nous savons comment estimer, marketer et vendre votre bien au juste prix — avec un accompagnement Pro et des frais réduits.",
      cta: 'En savoir plus',
      to: '/sell-with-us',
    },
    {
      key: 'rent',
      illustration: '/illustrations/rent.png?v=2',
      illustrationAlt: 'Icône Louer un logement',
      title: 'Louer',
      description:
        "Que vous cherchiez un appartement, un studio ou une maison, trouvez facilement la location qui vous correspond partout au Cameroun.",
      cta: 'Voir les locations',
      to: '/properties?purpose=rent',
    },
  ];

  const cards = services.map((s) => (
    <div
      key={s.key}
      className="group relative h-full bg-white rounded-2xl border border-stone-200 hover:border-stone-300 hover:shadow-lg transition-all duration-300 flex flex-col"
      data-testid={`quick-link-${s.key}`}
    >
      {/* Illustration — descriptive alt for WCAG 1.1.1 + SEO
          (BUG-009 fix). `aria-hidden` retiré : le texte alt est
          maintenant informatif et sera lu par les lecteurs
          d'écran (utile car l'illustration précède le H3). */}
      <div className="px-6 pt-6 pb-2 flex justify-center">
        <img
          src={s.illustration}
          alt={s.illustrationAlt}
          loading="lazy"
          className="w-full max-w-[200px] aspect-square object-contain transition-transform duration-300 group-hover:-translate-y-1"
        />
      </div>

      {/* Title — centered */}
      <h3 className="font-display text-2xl font-bold text-stone-900 text-center px-6 mb-3">
        {s.title}
      </h3>

      {/* Description — centered */}
      <p className="text-sm text-stone-600 leading-relaxed text-center px-6 mb-7 flex-1">
        {s.description}
      </p>

      {/* CTA outlined button at the bottom */}
      <div className="px-6 pb-6">
        <Link
          to={s.to}
          className="block w-full text-center text-sm font-semibold text-stone-900 border-2 border-stone-900 hover:bg-stone-900 hover:text-white rounded-md py-3 transition-colors"
          data-testid={`quick-link-cta-${s.key}`}
        >
          {s.cta}
        </Link>
      </div>
    </div>
  ));

  return (
    <section className="mb-20 bg-stone-100/70 rounded-3xl py-12 px-4 sm:px-8" data-testid="home-quick-links">
      <div className="mb-10 text-center">
        <h2 className="font-display text-3xl sm:text-4xl font-bold text-stone-900 leading-tight">
          Liens rapides
        </h2>
        <p className="text-stone-500 text-sm sm:text-base mt-2">
          Tous nos services immobiliers en un coup d&apos;œil.
        </p>
      </div>

      <MobileCarousel
        testId="quick-links"
        slides={cards}
        desktop={<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">{cards}</div>}
      />
    </section>
  );
};

const RegionCard = ({ region }) => (
  <Card
    className="border-stone-200 hover:border-orange-300 hover:shadow-md transition-all"
    data-testid={`region-${region.name}`}
  >
    <CardContent className="p-4">
      <div className="flex items-center gap-2 mb-3 pb-2 border-b border-stone-100">
        <MapPin className="h-4 w-4 text-orange-500 flex-shrink-0" />
        <Link
          to={`/properties?city=${encodeURIComponent(region.name)}`}
          className="font-display font-bold text-stone-900 hover:text-orange-600 transition-colors"
        >
          {region.name}
        </Link>
      </div>
      <ul className="space-y-1.5">
        {region.cities.map((city) => (
          <li key={city}>
            <Link
              to={`/properties?city=${encodeURIComponent(city)}`}
              className="text-sm text-stone-600 hover:text-orange-600 hover:translate-x-0.5 inline-flex items-center gap-1 transition-all"
              data-testid={`region-city-${city}`}
            >
              <ChevronRight className="h-3 w-3 text-stone-400" />
              {city}
            </Link>
          </li>
        ))}
      </ul>
    </CardContent>
  </Card>
);

const regionPairs = [];
for (let i = 0; i < regions.length; i += 2) regionPairs.push(regions.slice(i, i + 2));

const RECENT_KEY = 'realty237:recently_viewed';

const readRecent = () => {
  try { return JSON.parse(localStorage.getItem(RECENT_KEY)) || []; } catch { return []; }
};

const uniq = (arr) => [...new Set(arr.filter(Boolean))];

const Home = () => {
  const [allProperties, setAllProperties] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);

  // BUG-005 — Branded homepage title (overrides the generic shell title)
  usePageTitle('Realty237 — Achat, location et vente immobilière au Cameroun');
  useSocialMeta({
    title: 'Realty237 — Achat, location et vente immobilière au Cameroun',
    description: 'Trouvez votre maison, appartement, terrain, bureau ou salle de fête à vendre ou à louer à Douala, Yaoundé et partout au Cameroun. Annonces vérifiées, agents certifiés et estimation en ligne.',
    type: 'website',
  });

  useEffect(() => {
    let alive = true;
    propertyAPI.getFeatured()
      .then((res) => (Array.isArray(res.data) ? res.data : []))
      .catch(() => [])
      .then((list) => {
        if (!alive) return null;
        setAllProperties(list);
        // Recommendations: never repeat featured or already-viewed listings.
        const recent = readRecent();
        return propertyAPI.getRecommendations({
          exclude: uniq([...list, ...recent].map((p) => p._id || p.id)).join(','),
          cities: uniq(recent.map((p) => p.city)).slice(0, 4).join(','),
          types: uniq(recent.map((p) => p.type)).slice(0, 3).join(','),
          limit: 9,
        }).then((r) => alive && setRecommendations(Array.isArray(r.data) ? r.data : []));
      })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);


  return (
    <div className="min-h-dvh bg-paper">
      <HomeHero />
      <HomeQuickFilters />

      <div className="max-w-7xl mx-auto px-4 py-2 sm:py-4">

        {/* Quick Services (5 raccourcis icônes) — mobile: 3 cols pour éviter
            l'orpheline; la dernière carte est centrée via col-start-2 sur
            mobile pour un rendu équilibré (5 items = 3 + 2 centrés). */}
        <div className="grid grid-cols-3 md:grid-cols-5 gap-3 sm:gap-4 mb-14 sm:mb-28 mt-1 sm:mt-2">
          <Link to="/blog" className="flex flex-col items-center p-3 sm:p-4 rounded-2xl hover:bg-stone-100 transition-colors" data-testid="qs-advices">
            <div className="bg-orange-100 p-3 rounded-full mb-2">
              <BookOpen className="h-6 w-6 text-orange-500" />
            </div>
            <span className="text-xs sm:text-sm font-medium text-center leading-tight">Conseils immobiliers</span>
          </Link>
          <Link to="/loan-calculator" className="flex flex-col items-center p-3 sm:p-4 rounded-2xl hover:bg-stone-100 transition-colors" data-testid="qs-loan">
            <div className="bg-orange-100 p-3 rounded-full mb-2">
              <Calculator className="h-6 w-6 text-orange-500" />
            </div>
            <span className="text-xs sm:text-sm font-medium text-center leading-tight">Calculateur de prêt</span>
          </Link>
          <Link to="/agents" className="flex flex-col items-center p-3 sm:p-4 rounded-2xl hover:bg-stone-100 transition-colors" data-testid="qs-agents">
            <div className="bg-orange-100 p-3 rounded-full mb-2">
              <Users className="h-6 w-6 text-orange-500" />
            </div>
            <span className="text-xs sm:text-sm font-medium text-center leading-tight">Trouver un agent</span>
          </Link>
          <Link to="/properties?view=map" className="col-start-1 md:col-start-auto flex flex-col items-center p-3 sm:p-4 rounded-2xl hover:bg-stone-100 transition-colors" data-testid="qs-map">
            <div className="bg-orange-100 p-3 rounded-full mb-2">
              <Map className="h-6 w-6 text-orange-500" />
            </div>
            <span className="text-xs sm:text-sm font-medium text-center leading-tight">Carte interactive</span>
          </Link>
          <Link to="/estimation" className="col-span-2 md:col-span-1 flex flex-col items-center p-3 sm:p-4 rounded-2xl hover:bg-stone-100 transition-colors" data-testid="qs-estimation">
            <div className="bg-orange-100 p-3 rounded-full mb-2">
              <Building2 className="h-6 w-6 text-orange-500" />
            </div>
            <span className="text-xs sm:text-sm font-medium text-center leading-tight">Estimation gratuite</span>
          </Link>
        </div>

        {/* Section 1 — Propriétés en vedette (3 par page × 3 pages) */}
        <FeaturedSection properties={allProperties} loading={loading} />

        {/* Section "Récemment consultés" — visible uniquement si l'utilisateur a un historique */}
        <RecentlyViewedSection />

        {/* Section 2 — Recommandations (masquée s'il y a moins de 3 biens distincts) */}
        {(loading || recommendations.length >= 3) && (
          <RecommendationsSection properties={recommendations} loading={loading} />
        )}

        {/* Marketing ads carousel */}
        <HomeAdCarousel />

        {/* Section 3 — Liens rapides */}
        <QuickLinks />

        {/* Recherche par région */}
        <div className="mb-16">
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-stone-900 mb-6">
            Recherche par région
          </h2>
          <MobileCarousel
            testId="regions"
            slides={regionPairs.map((pair) => (
              <div className="space-y-4">
                {pair.map((region) => <RegionCard key={region.name} region={region} />)}
              </div>
            ))}
            desktop={
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {regions.map((region) => <RegionCard key={region.name} region={region} />)}
              </div>
            }
          />
        </div>

        {/* Conseils immobiliers — 3 derniers articles */}
        <HomeAdviceSection />
      </div>
    </div>
  );
};

export default Home;
